// src/scenes/CreateGameScene.js - REFACTORED
import Phaser from 'phaser';
import BaseMenuScene from "./BaseMenuScene.js";
import MenuUIUtils from "../ui/MenuUIUtils.js";
import { auth, db, isDatabaseConnected, forceReconnect } from '../firebaseConfig.js';
import { createGameSession } from "../services/multiplayerService.js";
import { createGame as createGameRest } from '../firebaseRestApi.js';
import { hasSpecialAccess, getSpecialAccessUserId, getSpecialAccessDisplayName } from '../services/specialAccessService.js';

// --- Helper Functions ---
function waitForAuth() {
    return new Promise((resolve, reject) => {
        if (auth.currentUser) return resolve(auth.currentUser);
        const timer = setTimeout(() => { unsubscribe(); reject(new Error("Auth timeout")); }, 5000);
        const unsubscribe = auth.onAuthStateChanged(user => { clearTimeout(timer); unsubscribe(); resolve(user); }, error => { clearTimeout(timer); unsubscribe(); reject(error); });
    });
}

async function createGameWithFallback(userId, displayName, gameMode, timeLimit) {
    try {
        return await createGameSession(userId, displayName, gameMode, timeLimit);
    } catch (error) {
        console.warn("RTDB createGameSession failed, attempting REST fallback:", error);
        return await createGameRest(userId, displayName, gameMode, timeLimit);
    }
}
// --- End Helper Functions ---

class CreateGameScene extends BaseMenuScene {
    constructor() {
        super({ key: "CreateGameScene" });
        this.gameMode = 'suddenDeath';
        this.timeLimit = 300;
        this.isCreatingGame = false;
    }

    init(data) {
        super.init(data);
        this.gameMode = 'suddenDeath';
        this.timeLimit = 300;
        this.isCreatingGame = false;
    }

    create() {
        this.setRibbonVisibility(false);
        super.create();
        this.clearMenuItems();

        const width = this.cameras.main.width;
        const height = this.cameras.main.height;
        const centerX = width / 2;

        // --- Layout Calculations ---
        let currentY = 80;
        const titleY = currentY; currentY += 40 + this.vSpacing * 3;
        const modeLabelY = currentY; currentY += 24 + this.vSpacing * 2;
        const modeButtonHeight = 50;
        const modeButtonY = currentY + modeButtonHeight / 2;
        const modeButtonGap = 280;
        currentY = modeButtonY + modeButtonHeight / 2 + this.vSpacing * 2;
        const timeControlsY = currentY + 40;
        const actionButtonContainerY = height - 100;
        const actionSpacing = 60;

        // --- Create UI Elements using MenuUIUtils ---
        this.uiElements.title = this.createThemedText(centerX, titleY, "CREATE GAME", 'sizeXl', MenuUIUtils.COLORS.primary);
        this.uiElements.modeLabel = this.createThemedText(centerX, modeLabelY, "GAME MODE:", 'sizeLg', MenuUIUtils.COLORS.textLight);
        this.uiElements.timeLimitText = this.createThemedText(centerX, timeControlsY - 20, "TIME LIMIT: 5 MIN", 'sizeLg', MenuUIUtils.COLORS.textLight).setVisible(false);

        const modeBtnWidth = 200;
        const timeBtnSize = 40;
        const timeBtnSpacing = 100;
        const actionBtnWidth = 250;
        const actionBtnHeight = 55;

        this.uiElements.suddenDeathBtn = this.createStyledButton(centerX - modeButtonGap / 2, modeButtonY, "SUDDEN DEATH", () => this.setGameMode('suddenDeath'), MenuUIUtils.COLORS.accent2, modeBtnWidth, modeButtonHeight);
        this.uiElements.timeAttackBtn = this.createStyledButton(centerX + modeButtonGap / 2, modeButtonY, "TIME ATTACK", () => this.setGameMode('timeAttack'), MenuUIUtils.COLORS.bgLight, modeBtnWidth, modeButtonHeight);
        
        this.uiElements.timeMinusBtn = this.createStyledButton(centerX - timeBtnSpacing, timeControlsY + 30, "-", () => { if (this.timeLimit > 60) { this.timeLimit -= 60; this.updateTimeLimitDisplay(); } }, MenuUIUtils.COLORS.bgLight, timeBtnSize, timeBtnSize, 'sizeLg').setVisible(false);
        this.uiElements.timePlusBtn = this.createStyledButton(centerX + timeBtnSpacing, timeControlsY + 30, "+", () => { if (this.timeLimit < 600) { this.timeLimit += 60; this.updateTimeLimitDisplay(); } }, MenuUIUtils.COLORS.bgLight, timeBtnSize, timeBtnSize, 'sizeLg').setVisible(false);
        
        this.uiElements.createBtn = this.createStyledButton(centerX, actionButtonContainerY - actionSpacing / 2, "CREATE GAME", this.handleCreateGame.bind(this), MenuUIUtils.COLORS.success, actionBtnWidth, actionBtnHeight, 'sizeLg');
        this.uiElements.backBtn = this.createStyledButton(centerX, actionButtonContainerY + actionSpacing / 2, "BACK", () => this.startScene("MultiplayerMenuScene"), MenuUIUtils.COLORS.bgLight, actionBtnWidth, actionBtnHeight, 'sizeLg');

        this.setGameMode('suddenDeath');
        this.handleResize();
    }

    async handleCreateGame() {
        if (this.isCreatingGame) return;
        this.isCreatingGame = true;
        const createBtn = this.uiElements.createBtn;
        this.setButtonLoading(createBtn, "CHECKING...");

        try {
            let isConnected = await isDatabaseConnected(5000);
            if (!isConnected) {
                await forceReconnect();
                await new Promise(resolve => setTimeout(resolve, 2000));
                isConnected = await isDatabaseConnected(3000);
            }
            if (!isConnected) throw new Error("Cannot connect to game server.");

            const user = await waitForAuth().catch(() => null);
            const specialAccess = hasSpecialAccess();
            if (!user && !specialAccess) throw new Error("Please sign in to create a game.");

            this.setButtonLoading(createBtn, "CREATING...");

            let userId = user ? user.uid : getSpecialAccessUserId();
            let displayName = "Player";
            if (user) {
                const doc = await db.collection("userProfiles").doc(user.uid).get();
                displayName = (doc.exists && doc.data().displayName) ? doc.data().displayName : (user.email || "Player");
            } else if (specialAccess) {
                displayName = getSpecialAccessDisplayName();
            }

            const gameCode = await createGameWithFallback(userId, displayName, this.gameMode, this.timeLimit);
            if (!gameCode) throw new Error("Server failed to create a game code.");

            this.isCreatingGame = false;
            this.startScene("LobbyScene", { gameCode, isHost: true });

        } catch (error) {
            this.showMessage(`Error: ${error.message}`, 3000, true);
            this.resetButtonState(createBtn, "CREATE GAME");
            this.isCreatingGame = false;
        }
    }

    setGameMode(mode) {
        this.gameMode = mode;
        const isSuddenDeath = mode === 'suddenDeath';
        const showTimeControls = !isSuddenDeath;

        this.resetButtonState(this.uiElements.suddenDeathBtn, "SUDDEN DEATH", isSuddenDeath ? MenuUIUtils.COLORS.accent2 : MenuUIUtils.COLORS.bgLight);
        this.resetButtonState(this.uiElements.timeAttackBtn, "TIME ATTACK", !isSuddenDeath ? MenuUIUtils.COLORS.accent2 : MenuUIUtils.COLORS.bgLight);

        this.uiElements.timeLimitText?.setVisible(showTimeControls);
        this.uiElements.timeMinusBtn?.setVisible(showTimeControls);
        this.uiElements.timePlusBtn?.setVisible(showTimeControls);
        if (showTimeControls) this.updateTimeLimitDisplay();
    }

    updateTimeLimitDisplay() {
        if (this.uiElements.timeLimitText) {
            const minutes = Math.floor(this.timeLimit / 60);
            this.uiElements.timeLimitText.setText(`TIME LIMIT: ${minutes} MIN`);
        }
    }
    
    handleResize() {
        super.handleResize();
        if (!this.cameras?.main) return;
        const width = this.cameras.main.width;
        const height = this.cameras.main.height;
        const centerX = width / 2;

        let currentY = 80;
        const titleY = currentY; currentY += 40 + this.vSpacing * 3;
        const modeLabelY = currentY; currentY += 24 + this.vSpacing * 2;
        const modeButtonHeight = 50;
        const modeButtonY = currentY + modeButtonHeight / 2;
        const modeButtonGap = 280;
        currentY = modeButtonY + modeButtonHeight / 2 + this.vSpacing * 2;
        const timeControlsY = currentY + 40;
        const actionButtonContainerY = height - 100;
        const actionSpacing = 60;
        const timeBtnSpacing = 100;
        const timeBtnControlY = timeControlsY + 30;

        this.uiElements.title?.setPosition(centerX, titleY);
        this.uiElements.modeLabel?.setPosition(centerX, modeLabelY);
        this.uiElements.suddenDeathBtn?.setPosition(centerX - modeButtonGap / 2, modeButtonY);
        this.uiElements.timeAttackBtn?.setPosition(centerX + modeButtonGap / 2, modeButtonY);

        if (this.uiElements.timeLimitText?.visible) {
            this.uiElements.timeLimitText.setPosition(centerX, timeControlsY - 20);
            this.uiElements.timeMinusBtn?.setPosition(centerX - timeBtnSpacing, timeBtnControlY);
            this.uiElements.timePlusBtn?.setPosition(centerX + timeBtnSpacing, timeBtnControlY);
        }

        this.uiElements.createBtn?.setPosition(centerX, actionButtonContainerY - actionSpacing / 2);
        this.uiElements.backBtn?.setPosition(centerX, actionButtonContainerY + actionSpacing / 2);
    }
}

export default CreateGameScene;